import { useRetainedState } from "@appretio/shared";
import { useCallback, useMemo } from "react";
import {
  INITIAL_INTAKE_SOURCE,
  type IntakeSource,
  isIntakeSource
} from "../../components/cubeDataTable/utils/intakeUtils";
import type { GridData } from "../../utils/summary/editPropagation";
import {
  INTAKE_COMPONENT_METRICS,
  OPENING_STOCK_COLUMNS
} from "../../utils/summary/salesKpiDerivations";
import { type CubePage, pageScope } from "../useCubeSelectionState";
import {
  snapshotIntakeCache,
  type SummaryIntakeCache,
  type SummaryIntakeCaches
} from "./summaryGridHelpers";

/** Parameters for the summary intake state hook. */
interface UseSummaryIntakeStateParams {
  /** The page whose intake state this is; retained per page with its grid. */
  page: CubePage;
  /** Current grid data (null before first response). */
  gridData: GridData | null;
  /** Setter to update grid data when swapping intake values. */
  setGridData: React.Dispatch<React.SetStateAction<GridData | null>>;
  /** Server-owned base layer (padded payload columns). */
  serverColumns: Record<string, (number | null)[][]>;
  /** Setter to swap server-owned opening stock (BOP|PTD) between variants. */
  setServerColumns: React.Dispatch<
    React.SetStateAction<Record<string, (number | null)[][]>>
  >;
  /** Marks POTB unanswerable — the swap moves OTB, which POTB scans. Not an unsaved edit: the summary save stores sales only. */
  markPotbStale: () => void;
}

/** ``prev`` with the intake component values of ``sourceCache`` swapped in. */
function withIntakeValues(
  prev: GridData,
  sourceCache: SummaryIntakeCache
): GridData {
  const ptd = prev.ptd.map((t) => t.map((r) => ({ ...r })));
  const ptg = prev.ptg.map((t) => t.map((r) => ({ ...r })));

  // Swap the intake component values (client-owned irreducibles).
  // Totals and stock (EOP/OTB) are derived at render, so no per-cell
  // recalculation is needed here. Only the PTD/PTG slices exist in grid
  // state — payload columns for other aggregations (e.g. TTL, which
  // derives from PTD+PTG at render) must not be written into PTG.
  for (const [col, newTables] of Object.entries(sourceCache)) {
    const sepIdx = col.lastIndexOf("|");
    if (sepIdx === -1) continue;
    const metric = col.slice(0, sepIdx);
    const colAgg = col.slice(sepIdx + 1);
    if (!INTAKE_COMPONENT_METRICS.has(metric)) continue;
    if (colAgg !== "PTD" && colAgg !== "PTG") continue;

    const slice = colAgg === "PTD" ? ptd : ptg;
    for (let ti = 0; ti < slice.length; ti++) {
      for (let ri = 0; ri < slice[ti].length; ri++) {
        const newVal = newTables[ti]?.[ri] ?? 0;
        slice[ti][ri][metric] = newVal;
      }
    }
  }

  return { ptd, ptg };
}

/** Manages the intake source toggle and one toggle-column cache per source. */
export function useSummaryIntakeState({
  page,
  gridData,
  setGridData,
  serverColumns,
  setServerColumns,
  markPotbStale
}: UseSummaryIntakeStateParams) {
  // Retained alongside the grid data they were snapshotted from, so a
  // restored grid keeps a consistent intake mode + cache pair.
  const scope = pageScope(page);
  const [intakeMode, setIntakeMode] = useRetainedState<IntakeSource>(
    scope,
    "intakeMode",
    INITIAL_INTAKE_SOURCE
  );
  const [intakeCaches, setIntakeCaches] = useRetainedState<SummaryIntakeCaches>(
    scope,
    "intakeCaches",
    {}
  );
  const availableIntakeSources = useMemo(
    () => Object.keys(intakeCaches).filter(isIntakeSource),
    [intakeCaches]
  );

  const handleIntakeToggle = useCallback(
    function handleIntakeToggle(mode: IntakeSource) {
      if (mode === intakeMode || !gridData) return;

      const sourceCache = intakeCaches[mode];
      if (!sourceCache) return;

      setGridData((prev) =>
        prev ? withIntakeValues(prev, sourceCache) : prev
      );

      // Swap the server-owned opening stock (BOP|PTD); present in the caches
      // only for future years (see OPENING_STOCK_COLUMNS).
      const openingColumns = Object.entries(sourceCache).filter(([col]) =>
        OPENING_STOCK_COLUMNS.has(col)
      );
      const swapOpening = openingColumns.length > 0;
      if (swapOpening) {
        setServerColumns((prev) => {
          const next = { ...prev };
          for (const [col, values] of openingColumns) {
            next[col] = values;
          }
          return next;
        });
      }

      const snapshot = snapshotIntakeCache(
        gridData,
        swapOpening ? serverColumns : {}
      );
      setIntakeCaches((prev) => ({ ...prev, [intakeMode]: snapshot }));

      setIntakeMode(mode);
      markPotbStale();
    },
    [
      gridData,
      intakeMode,
      setIntakeMode,
      intakeCaches,
      setIntakeCaches,
      setGridData,
      serverColumns,
      setServerColumns,
      markPotbStale
    ]
  );

  // No loaded data, no caches to swap between.
  const reset = useCallback(() => {
    setIntakeCaches({});
    setIntakeMode(INITIAL_INTAKE_SOURCE);
  }, [setIntakeCaches, setIntakeMode]);

  return {
    intakeMode,
    setIntakeMode,
    intakeCaches,
    setIntakeCaches,
    availableIntakeSources,
    handleIntakeToggle,
    reset
  };
}
